import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { needsEmailVerification, useAuth } from '../context/AuthContext';
import Seo from '../seo/Seo';

function VerifyEmailPage() {
  const { user, loading, isAuthenticated, resendEmailVerification, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const from = location.state?.from?.pathname || '/projects';

  const [resendInfo, setResendInfo] = useState('');
  const [checkMessage, setCheckMessage] = useState('');
  const [reloadLoading, setReloadLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!needsEmailVerification(user)) {
    return <Navigate to={from} replace />;
  }

  const handleVerifiedClick = async () => {
    try {
      setError('');
      setCheckMessage('');
      setReloadLoading(true);
      const refreshed = await refreshUser();
      if (refreshed?.emailVerified) {
        navigate(from, { replace: true });
      } else {
        setCheckMessage(
          'Email is not verified yet. Please check your inbox and click the verification link.'
        );
      }
    } catch {
      setError('Unable to refresh your account. Please try again.');
    } finally {
      setReloadLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setError('');
      setResendInfo('');
      setResendLoading(true);
      await resendEmailVerification();
      setResendInfo(
        'Verification email sent from Firebase. Check inbox and spam; delivery may take a few minutes.'
      );
    } catch (err) {
      if (err?.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (
        err?.code === 'auth/unauthorized-continue-uri' ||
        err?.code === 'auth/invalid-continue-uri'
      ) {
        setError(
          'Cannot resend: this domain is not allowed for Firebase redirects. Add it under Firebase Authentication → Authorized domains.'
        );
      } else {
        setError('Unable to resend the email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    setError('');
    await logout();
    navigate('/login', { replace: true });
  };

  const handleSignOut = async () => {
    setError('');
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="page landing-bg verify-email-page">
      <Seo
        title="Verify Email | MyRoomsAI"
        description="Verify your email address to continue using MyRoomsAI and access your AI room design projects."
        canonicalPath="/verify-email"
        noindex
      />
      <Navbar />
      <main className="auth-page">
        <div className="auth-card card">
          <h1>Verify your email</h1>
          <p>Please verify your email before continuing.</p>

          <div className="auth-form">
            <button
              type="button"
              className="btn btn-primary full-width"
              onClick={handleVerifiedClick}
              disabled={reloadLoading}
            >
              {reloadLoading ? 'Checking…' : 'I have verified my email'}
            </button>
            <button
              type="button"
              className="btn btn-primary full-width"
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>
            <button type="button" className="btn btn-ghost full-width" onClick={handleBackToLogin}>
              Back to Login
            </button>
            <button type="button" className="btn btn-ghost full-width" onClick={handleSignOut}>
              Sign out
            </button>
            {checkMessage ? <p>{checkMessage}</p> : null}
            {resendInfo ? <p>{resendInfo}</p> : null}
            {error ? <p className="auth-error">{error}</p> : null}
          </div>

          <p className="switch-auth">
            <Link to="/login">Login</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default VerifyEmailPage;
